// Shared Nuxt UI app.config block. Each app re-exports it from its
// `app.config.ts` via `defineAppConfig({ ui: sharedAppConfigUi })`.
// Both apps register the `icon:` and `home:` collections from `shared/icons*`
// in their `nuxt.config.ts > icon.customCollections`, so the `icons` mapping
// below resolves in both.
//
// Note: no explicit type annotation. Nuxt UI's `defineAppConfig` augmentation
// relies on the literal shape of the `ui:` object to type-extend component
// variants (e.g. `select.variants.variant.link`). A `Record<string, unknown>`
// annotation would erase those literal types and break consumers.

// Shared appearance for input-like components (input, inputNumber, inputDate,
// inputTime, select, selectMenu). At Nuxt UI's base theme level all of them
// already extend `input` (see node_modules/@nuxt/ui/dist/shared/ui.*.mjs:
// `inputDate(options)` ends with `}, input(options));`, same for the others).
// But at app.config.ts level each component is overridden independently:
// our `input.compoundVariants` are NOT inherited by `inputDate` / `select` /…
// so we factor the appearance here and spread it into each component below.

// Focus des inputs : mise en forme (INPUT_FOCUS*) et couleur (INPUT_FOCUS_COLOR*)
// séparées pour ne dupliquer ni l'une ni l'autre. Couleur outline non préfixée
// (l'outline n'est visible qu'au focus) ; couleur du ring préfixée (le ring a une
// couleur au repos). Classes atomiques (pas une @utility CSS) pour que
// tailwind-merge puisse les écraser entre variants et overrides `:ui`.

export const INPUT_FOCUS = "focus:ring focus:outline-solid focus:outline-3";
export const INPUT_FOCUS_VISIBLE = "focus-visible:ring focus-visible:outline-solid! focus-visible:outline-3";
export const INPUT_HAS_FOCUS_VISIBLE = "has-focus-visible:ring has-focus-visible:outline-solid! has-focus-visible:outline-3";

export const INPUT_FOCUS_COLOR_PRIMARY =
  "outline-primary-300/80 focus:ring-primary-500 focus-visible:ring-primary-500 has-focus-visible:ring-primary-500 in-elevated:outline-primary-300/25";
export const INPUT_FOCUS_COLOR_ERROR =
  "outline-error-100 focus:ring-error focus-visible:ring-error has-focus-visible:ring-error in-elevated:outline-error-200/25";

export const BUTTON_FOCUS =
  "focus:outline-none focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-primary has-focus-visible:outline-2 outline-primary in-inverted:focus-visible:outline-default/80 in-inverted:has-focus-visible:outline-default";
export const BUTTON_FOCUS_DARK = "focus-visible:outline-offset-1 focus-visible:outline-2 focus-visible:outline-default/80";
export const BUTTON_FOCUS_OFFSET = `${BUTTON_FOCUS} focus-visible:outline-offset-2 has-focus-visible:outline-offset-2`;

const inputAppearance = {
  slots: {
    root: "",
    base: `transition-none text-base/8 bg-white rounded ring ring-blue-500 placeholder:text-primary-600 ${INPUT_FOCUS} ${INPUT_FOCUS_VISIBLE} ${INPUT_FOCUS_COLOR_PRIMARY}`,
    leadingIcon: "text-current shrink-0",
    trailingIcon: "text-current shrink-0",
    leading: "min-w-8 justify-center",
    trailing: "min-w-8 justify-center",
    content: "",
  },
  variants: {
    size: {
      md: {
        base: "px-3 py-1 font-normal",
        leadingIcon: "size-6",
        trailingIcon: "size-6",
        leading: "ps-1.5",
        trailing: "pe-1.5",
      },
      lg: {
        base: "px-4 py-2 font-semibold",
        leadingIcon: "size-4",
        trailingIcon: "size-4",
        leading: "ps-3",
        trailing: "pe-3",
      },
      xl: {
        base: "px-4 py-1.5 font-semibold",
        leadingIcon: "size-4 md:size-6",
        trailingIcon: "size-4 md:size-6",
        leading: "ps-4",
        trailing: "pe-4",
      },
    },
  },
  // `select`/`selectMenu` réécrivent `focus-visible:` en `focus:` côté thème Nuxt UI, d'où la duplication des deux préfixes.
  compoundVariants: [
    {
      variant: ["outline", "subtle", "soft"],
      class: { base: `${INPUT_FOCUS} ${INPUT_FOCUS_VISIBLE}` },
    },
    {
      color: "primary",
      variant: ["outline", "subtle"],
      class: {
        base: `text-primary placeholder:text-primary-600 ring ring-inset ring-primary-400 hover:not-disabled:ring-primary-500/80 disabled:opacity-100 disabled:bg-gray-100 disabled:text-gray-400 disabled:placeholder-gray-300 disabled:ring-gray-200`,
      },
    },
    {
      color: "primary",
      highlight: true,
      class: { base: "ring ring-inset ring-primary-500 hover:not-disabled:ring-primary-600" },
    },
    {
      color: "primary",
      variant: "soft",
      class: {
        base: "text-primary placeholder:text-gray-700/60 rounded-lg bg-primary-300 hover:not-disabled:bg-primary-300 focus:bg-primary-300 hover:not-disabled:ring hover:not-disabled:ring-primary-500 ring-inset! ring-0 focus:ring outline-primary-400/50",
      },
    },
    {
      color: "primary",
      variant: "subtle",
      class: {
        base: "placeholder:text-gray-700/60 rounded-lg bg-primary-300 hover:not-disabled:bg-primary-300 focus:bg-primary-300 hover:not-disabled:ring ring-inset! ring-0 focus:ring focus:ring-primary-500/60",
      },
    },
    {
      color: "primary",
      variant: ["outline", "subtle"],
      class: {
        base: `ring ring-inset ${INPUT_FOCUS_COLOR_PRIMARY}`,
      },
    },
    {
      color: "error",
      variant: ["outline", "subtle"],
      class: {
        base: `ring ring-inset ${INPUT_FOCUS_COLOR_ERROR}`,
      },
    },
    {
      color: "error",
      highlight: true,
      class: { base: "text-primary ring ring-error! outline-error-100" },
    },
    {
      trailing: true,
      size: "md",
      class: "pe-12",
    },
    {
      trailing: true,
      size: "lg",
      class: "pe-12",
    },
    {
      trailing: true,
      size: "xl",
      class: "pe-12 sm:pe-13",
    },
    {
      fixed: false,
      size: "md",
      class: "text-base/8 md:text-base/8",
    },
    {
      fixed: false,
      size: "lg",
      class: "text-lg/7 md:text-lg/7",
    },
    { fixed: false, size: "xl", class: "text-lg/9 md:text-2xl/9" },
  ],
};

// `select` / `selectMenu` héritent de `inputAppearance.compoundVariants` mais
// ne doivent PAS recevoir `focus:outline-solid` — on en dérive une version
// nettoyée à spread à leur place.
const selectInputCompoundVariants = inputAppearance.compoundVariants.map((cv) => {
  if (cv.class && typeof cv.class === "object" && "base" in cv.class && typeof cv.class.base === "string") {
    const base = cv.class.base
      .replace(/\s*focus:outline-solid!?/g, "")
      .replace(/\s{2,}/g, " ")
      .trim();
    if (base !== cv.class.base) {
      return { ...cv, class: { ...cv.class, base } };
    }
  }
  return cv;
});

// `inputDate` et `inputTime` partagent les overrides de leur slot `segment`.
const dateTimeSegmentCompounds = [
  {
    variant: "outline",
    class: {
      segment:
        "data-placeholder:text-primary-600 data-placeholder:tracking-wide hover:not-data-disabled:bg-blue-400/30 hover:not-data-disabled:text-primary focus:bg-blue-300 focus:font-semibold focus:data-placeholder:text-primary focus:data-placeholder:font-semibold group-hover:focus:bg-blue-400/40",
    },
  },
  {
    variant: "soft",
    class: {
      segment:
        "data-placeholder:text-primary-600 data-placeholder:tracking-wide hover:not-data-disabled:bg-blue-400/30 hover:not-data-disabled:text-primary hover:not-data-disabled:data-placeholder:text-primary-500 focus:bg-blue-100/80 focus:font-semibold focus:data-placeholder:text-primary focus:data-placeholder:font-semibold group-hover:focus:bg-blue-100/80",
    },
  },
];

// Pendant data-* des styles `disabled:` des autres inputs : les champs date/heure rendent
// un <div> + segments contenteditable, jamais ciblés par la pseudo-classe `:disabled` —
// Reka pose l'attribut `data-disabled` sur le root et les segments.
const DATE_TIME_DISABLED_BASE =
  "data-disabled:cursor-not-allowed data-disabled:bg-gray-100 data-disabled:text-gray-400 data-disabled:ring-gray-200 hover:data-disabled:ring-gray-200";
const DATE_TIME_DISABLED_SEGMENT = "not-data-[segment=literal]:data-disabled:cursor-not-allowed data-disabled:data-placeholder:text-gray-300";

// `select` et `selectMenu` partagent la même apparence : on factorise ici.
const selectAppearance = {
  slots: {
    ...inputAppearance.slots,
    base: `${inputAppearance.slots.base} w-full`,
    value: "text-inherit leading-[2]",
    placeholder: "text-inherit leading-[2]",
    trailing: "aspect-3/4 justify-center",
    content: "w-fit min-w-(--reka-select-trigger-width) ring-blue-400 max-w-3xs",
    group: "px-0 mx-0",
    itemTrailingIcon: "hidden",
    item: "px-4! text-primary font-semibold before:rounded-none data-highlighted:not-data-disabled:before:bg-elevated data-highlighted:not-data-disabled:text-primary! data-[state=checked]:text-blue-700! data-[state=checked]:underline data-[state=checked]:underline-offset-4 transition-none before:transition-none before:inset-x-0",
  },
  variants: {
    ...inputAppearance.variants,
    size: {
      md: { base: "px-3 py-1", leadingIcon: "size-4", leading: "pe-3.5", trailingIcon: "size-4", trailing: "pe-3.5" },
      lg: {
        base: "px-3 py-1.5",
        leadingIcon: "size-4",
        leading: "pe-3.5",
        trailingIcon: "size-4",
        trailing: "pe-3.5",
        item: "px-4",
        label: "text-base px-4 pb-0",
      },
      xl: {
        base: "px-4 py-1.5 font-semibold",
        value: "text-lg/6 md:text-2xl/6",
        placeholder: "text-lg/6 md:text-2xl/6",
        leadingIcon: "size-4",
        leading: "pe-3.5",
        trailingIcon: "size-4",
        trailing: "pe-3.5",
        item: "px-4",
        label: "text-base px-4 pb-0",
      },
    },
    variant: {
      outline: "hover:bg-white",
      link: {
        root: "w-auto",
        base: `text-base! h-6.5! focus:outline-none underline underline-offset-6 bg-transparent! ring-0! py-0 pl-1 -mx-1 pe-6.25! rounded-xs! border-0! font-semibold text-primary hover:not-disabled:text-primary-700 gap-1.5 focus-visible:ring-0 ${BUTTON_FOCUS_OFFSET}`,
        trailing: "aspect-auto! flex items-center pe-0.75",
        trailingIcon: "size-4!",
      },
    },
    // Supprime la transition d'ouverture/fermeture du popover (scale-in/out)
    position: {
      popper: {
        content: "data-[state=open]:animate-none data-[state=closed]:animate-none",
      },
    },
  },
  compoundVariants: [
    ...selectInputCompoundVariants,
    {
      color: "primary",
      variant: "outline",
      class: { placeholder: "text-primary-600" },
    },
    {
      color: "primary",
      variant: ["soft", "subtle"],
      class: { placeholder: "text-gray-700/60" },
    },
    {
      fieldGroup: ["horizontal", "vertical"],
      size: ["md", "xl"],
      class: {
        value: "text-base/6 md:text-base/8",
        placeholder: "text-base/6 md:text-base/8",
      },
    },
  ],
};

const nuxtUiUnusedIconFallbacks = {
  dark: "icon:help",
  light: "icon:help",
  system: "icon:help",
  folder: "icon:list",
  folderOpen: "icon:list",
  hash: "icon:list",
  panelClose: "icon:close",
  panelOpen: "icon:menu",
  reload: "icon:go",
  star: "icon:valid",
  stop: "icon:close",
};

export const sharedUiIcons = {
  arrowLeft: "icon:arrowLeft",
  arrowRight: "icon:arrowRight",
  arrowUp: "icon:chevronUp",
  arrowDown: "icon:chevronDown",
  caution: "icon:warning-circle",
  check: "icon:check",
  chevronDoubleLeft: "icon:arrowLeft",
  chevronDoubleRight: "icon:arrowRight",
  chevronDown: "icon:chevronDown",
  chevronLeft: "icon:chevronLeft",
  chevronRight: "icon:chevronRight",
  chevronUp: "icon:chevronUp",
  close: "icon:close",
  copy: "icon:duplicate",
  copyCheck: "icon:valid",
  drag: "icon:dots-vertical",
  ellipsis: "icon:dots-horizontal",
  error: "icon:invalid",
  external: "icon:external",
  eye: "icon:eye",
  eyeOff: "icon:hide",
  file: "icon:image",
  info: "icon:help",
  loading: "icon:loading",
  menu: "icon:menu",
  minus: "icon:minus",
  plus: "icon:plus",
  search: "icon:search",
  success: "icon:valid",
  tip: "icon:help",
  upload: "icon:upload",
  warning: "icon:warning",
  ...nuxtUiUnusedIconFallbacks,
};

export const sharedAppConfigUi = {
  icons: sharedUiIcons,
  link: {
    base: `in-inverted:text-white in-inverted:hover:text-blue-400 underline underline-offset-[30%] decoration-1 rounded-[1px] ${BUTTON_FOCUS_OFFSET}`,
    variants: {
      active: {
        false: "text-default",
      },
    },
  },
  colors: {
    primary: "blue",
    secondary: "lagoon",
    success: "green",
    error: "red",
    neutral: "gray",
    blue: "blue",
  },
  container: {
    base: "w-full max-w-(--ui-container) mx-auto px-4 sm:px-6 lg:px-8 grow",
  },
  header: {
    slots: {
      root: "bg-default border-secondary backdrop-blur-none h-auto min-h-(--ui-header-height)",
      body: "grow pt-6 pb-10",
      header: "border-secondary",
      container: "min-h-(--ui-header-height) lg:items-stretch gap-4",
      left: "items-stretch gap-3",
      right: "items-stretch gap-x-2 md:gap-x-4 ",
      content: "max-w-auto",
    },
    variants: {
      toggleSide: {
        right: { toggle: "me-0" },
      },
    },
  },
  navigationMenu: {
    slots: {
      root: "items-stretch!",
      body: "max-lg:pb-10",
      list: "gap-x-0.5 lg:h-full",
      item: "lg:h-full lg:py-0! max-lg:border-b max-lg:border-blue-300 relative flex items-center",
      link: "relative max-lg:w-auto max-lg:px-0 max-lg:py-4 max-lg:text-2xl text-base font-semibold relative lg:static",
    },
    variants: {
      active: {
        true: {
          link: "text-blue-700! after:absolute after:-bottom-px after:inset-x-0 lg:after:inset-x-2.5 after:h-1 after:bg-blue-700",
        },
        false: {
          link: "text-default",
        },
      },
      orientation: {
        horizontal: {
          link: "before:inset-y-2",
        },
      },
    },
  },
  breadcrumb: {
    slots: {
      separator: "text-default",
      separatorIcon: "size-3.5 -mt-px text-default",
    },
    variants: {
      active: {
        true: {
          link: "text-default no-underline font-semibold",
        },
        false: {
          link: `text-default underline underline-offset-[.18em] font-semibold rounded-[1px] ${BUTTON_FOCUS_OFFSET}`,
        },
      },
    },
  },
  formField: {
    slots: {
      labelWrapper: "mb-2",
      label: "block text-base font-semibold leading-tight text-primary",
      error: "text-error text-sm leading-tight mt-1",
    },
  },
  input: {
    slots: { ...inputAppearance.slots, root: "w-full" },
    variants: { ...inputAppearance.variants },
    compoundVariants: [...inputAppearance.compoundVariants],
  },
  inputNumber: {
    slots: {
      ...inputAppearance.slots,
      root: "w-full",
      base: `${inputAppearance.slots.base} tracking-wide`,
      increment: "in-data-disabled:[&>button]:text-gray-400!",
      decrement: "in-data-disabled:[&>button]:text-gray-400!",
    },
    variants: {
      ...inputAppearance.variants,
      orientation: {
        vertical: {
          increment: "h-1/2 w-8 justify-center items-end top-0 pe-0 inset-e-1 [&>button]:aspect-square [&>button]:p-0.75 scale-80",
          decrement: "h-1/2 w-8 justify-center items-start bottom-0 pe-0 inset-e-1 [&>button]:aspect-square [&>button]:p-0.75 scale-80",
        },
        horizontal: {
          increment: "flex items-center justify-center [&>button]:size-full [&>button]:px-3.25 [&>button]:w-auto",
          decrement: "flex items-center justify-center [&>button]:size-full [&>button]:px-3.25 [&>button]:w-auto",
        },
      },
      size: {
        md: "px-3 py-1 gap-1.5",
        lg: "px-4 py-2 gap-2",
        xl: "px-4 py-1.5 gap-2",
      },
    },
    compoundVariants: [
      ...inputAppearance.compoundVariants,
      {
        increment: true,
        size: "md",
        class: "pe-11",
      },
      {
        increment: true,
        size: "lg",
        class: "pe-11",
      },
      {
        increment: true,
        size: "xl",
        class: "pe-11",
      },
    ],
  },
  inputDate: {
    slots: {
      ...inputAppearance.slots,
      base: `tabular-nums tracking-wide rounded ps-1.5 py-1.5! pe-12! leading-[1.75]! ${DATE_TIME_DISABLED_BASE}`,
      segment: `whitespace-nowrap rounded-sm! data-[segment=literal]:pointer-events-none not-data-[segment=literal]:w-auto! not-data-[segment=literal]:px-1 not-data-[segment=literal]:min-w-7 not-data-[segment=literal]:cursor-pointer data-[segment=literal]:aria-disabled:text-gray-400 ${DATE_TIME_DISABLED_SEGMENT}`,
      // Le trailing vit hors du conteneur porteur de data-disabled : ciblé via le root group.
      trailingIcon: `${inputAppearance.slots.trailingIcon} group-has-data-disabled:text-gray-400`,
    },
    variants: {
      ...inputAppearance.variants,
      size: {
        ...inputAppearance.variants.size,
      },
    },
    compoundVariants: [...inputAppearance.compoundVariants, ...dateTimeSegmentCompounds],
  },
  inputTime: {
    slots: {
      ...inputAppearance.slots,
      base: `tabular-nums tracking-wide rounded ps-1.5 py-1.5! pe-11! leading-[1.75]! ${DATE_TIME_DISABLED_BASE}`,
      segment: `whitespace-nowrap rounded-sm! data-[segment=literal]:pointer-events-none not-data-[segment=literal]:w-auto! not-data-[segment=literal]:px-1 not-data-[segment=literal]:min-w-7 not-data-[segment=literal]:cursor-pointer data-[segment=literal]:aria-disabled:text-gray-400 ${DATE_TIME_DISABLED_SEGMENT}`,
      // Le trailing vit hors du conteneur porteur de data-disabled : ciblé via le root group.
      trailingIcon: `${inputAppearance.slots.trailingIcon} group-has-data-disabled:text-gray-400`,
    },
    variants: {
      ...inputAppearance.variants,
      size: {
        ...inputAppearance.variants.size,
      },
    },
    compoundVariants: [...inputAppearance.compoundVariants, ...dateTimeSegmentCompounds],
  },
  inputMenu: {
    slots: {
      ...inputAppearance.slots,
      root: "rounded-lg",
      content: "ring-blue-400 data-[state=open]:animate-none data-[state=closed]:animate-none",
      group: "px-0",
      viewport: "scroll-py-0",
      label: "text-base text-blue-600 not-first:mt-4",
      item: "before:rounded-none data-highlighted:not-data-disabled:before:bg-elevated [data-selected=true]:underline [data-selected=true]:text-highlighted ",
    },
    variants: {
      ...inputAppearance.variants,
      size: {
        ...inputAppearance.variants.size,
        md: { base: "px-3 py-1", leadingIcon: "size-4", leading: "pe-3.5", trailingIcon: "size-4", trailing: "pe-3.5" },
        lg: {
          base: "px-4 py-2",
          leadingIcon: "size-4",
          leading: "pe-3.5",
          trailingIcon: "size-4",
          trailing: "pe-3.5",
          item: "px-4",
          label: "text-base px-4 pb-0",
        },
        xl: {
          base: "px-4 py-1.5 font-semibold",
          leadingIcon: "size-4 md:size-6",
          leading: "pe-3.5",
          trailingIcon: "size-4 md:size-6",
          trailing: "pe-3.5",
          item: "px-4",
          label: "text-base px-4 pb-0",
        },
      },
      multiple: {
        false: {
          base: "placeholder:text-primary-600 focus:outline-solid",
        },
      },
    },
    compoundVariants: [
      ...inputAppearance.compoundVariants,
      {
        color: "primary",
        variant: ["outline", "subtle"],
        class: {
          root: `${INPUT_FOCUS} ${INPUT_FOCUS_VISIBLE} ${INPUT_FOCUS_COLOR_PRIMARY}`,
        },
      },
      {
        color: "error",
        variant: ["outline", "subtle"],
        class: {
          root: `${INPUT_FOCUS} ${INPUT_FOCUS_VISIBLE} ${INPUT_FOCUS_COLOR_ERROR}`,
        },
      },
    ],
  },
  textarea: {
    slots: { ...inputAppearance.slots, base: `${inputAppearance.slots.base} min-h-[2lh] max-h-[15lh]` },
    variants: { ...inputAppearance.variants },
    compoundVariants: [...inputAppearance.compoundVariants],
  },
  select: selectAppearance,
  selectMenu: selectAppearance,
  switch: {
    slots: {
      label: "text-current",
      base: "group/switch transition-none cursor-pointer data-[state=unchecked]:bg-transparent border",
      thumb: "bg-[var(--bg-current)] transition-transform duration-150 group-hover/switch:scale-125",
      wrapper: "ms-4",
    },
    variants: {
      loading: {
        true: {
          icon: "hidden",
        },
      },
      color: {
        primary: {
          base: "border-current data-[state=checked]:bg-current in-inverted:text-secondary",
          thumb: "data-[state=unchecked]:bg-current",
        },
        secondary: {
          base: "border-secondary",
          thumb: "data-[state=unchecked]:bg-secondary",
        },
        success: {
          base: "border-success",
          thumb: "data-[state=unchecked]:bg-success",
        },
        error: {
          base: "border-error",
          thumb: "data-[state=unchecked]:bg-error",
        },
        neutral: {
          base: "border-inverted",
          thumb: "data-[state=unchecked]:bg-inverted",
        },
      },
      size: {
        xs: {
          thumb: "size-2.5 data-[state=checked]:translate-x-2.5 data-[state=checked]:rtl:-translate-x-2.5",
        },
        sm: {
          base: "w-7.5 p-0.5",
          containter: "h-3.5",
          thumb: "size-2 data-[state=checked]:translate-x-3 data-[state=checked]:rtl:-translate-x-3",
        },
        md: {
          base: "w-8 p-0.5",
          containter: "h-4",
          thumb: "size-2 m-px data-[state=checked]:translate-x-4 data-[state=checked]:rtl:-translate-x-4",
        },
        lg: {
          thumb: "size-2.5 data-[state=checked]:translate-x-4 data-[state=checked]:rtl:-translate-x-4",
        },
        xl: {
          thumb: "size-4.5 data-[state=checked]:translate-x-4.5 data-[state=checked]:rtl:-translate-x-4.5",
        },
      },
    },
  },
  checkbox: {
    slots: {
      root: "items-center cursor-pointer",
      base: "bg-white rounded-xs ring-blue-600",
      label: "text-base/6 text-primary cursor-pointer",
    },
    variants: {
      variant: {
        // Le fond coloré du `card` reprend le bouton soft : aplat bleu, sans bordure.
        card: {
          root: "bg-primary-200 hover:bg-primary-300 border-0 rounded",
        },
      },
    },
    compoundVariants: [
      // Padding du `card` posé ici (et non dans `variants`) car le thème de base le fixe via
      // un compoundVariant `p-3.5` qui, sinon, gagnerait sur le slot après twMerge.
      { size: "md", variant: "card", class: { root: "px-4 py-2" } },
      // Coché : la case reste blanche avec un ✓ bleu foncé (comme la checkbox native du modèle),
      // au lieu de l'aplat `bg-primary`. `size-3.5` laisse une marge autour du ✓ comme le natif.
      // Posé en compoundVariant pour battre le variant couleur.
      { class: { indicator: "bg-transparent text-primary", icon: "size-3 [&_path]:stroke-[2.8px]" } },
    ],
  },
  button: {
    slots: {
      base: "select-none not-disabled:cursor-pointer disabled:pointer-events-none font-semibold leading-none outline-primary-200 rounded transition-none",
    },
    variants: {
      loading: {
        true: {
          base: "justify-center",
          label: "hidden",
        },
      },
      size: {
        xs: {
          base: "text-base/5.5 px-3 py-1 gap-1 rounded-sm",
          leadingIcon: "size-3 -ml-0.5",
          trailingIcon: "size-3 -mr-0.5",
        },
        sm: {
          base: "text-sm px-4 py-2 gap-1 rounded-sm",
          leadingIcon: "size-3.5 -ml-0.5",
          trailingIcon: "size-3.5 -mr-0.5",
        },
        md: {
          base: "text-base/6 px-4 py-2 gap-2",
          leadingIcon: "size-4 -ml-0.5",
          trailingIcon: "size-4 -mr-0.5",
        },
        lg: {
          base: "text-lg px-5 py-3.5 gap-2",
          leadingIcon: "size-6 -ml-1",
          trailingIcon: "size-6 -mr-1",
        },
        xl: {
          base: "text-xl px-5 py-4 gap-2",
          leadingIcon: "size-6 -ml-1",
          trailingIcon: "size-6 -mr-1",
        },
      },
      square: {
        true: "[&>svg]:m-0!",
      },
    },
    compoundVariants: [
      {
        variant: ["soft", "subtle", "ghost"],
        class: `${BUTTON_FOCUS}`,
      },
      {
        variant: ["solid", "outline", "link"],
        class: `${BUTTON_FOCUS_OFFSET}`,
      },
      {
        variant: ["outline"],
        class: "focus-visible:ring has-focus-visible:ring",
      },
      { variant: ["link"], class: "focus-visible:ring-0" },
      {
        color: "primary",
        variant: "solid",
        class:
          "hover:not-disabled:bg-secondary hover:not-disabled:text-primary active:bg-secondary active:text-primary in-inverted:text-default in-inverted:active:text-default",
      },
      {
        color: "secondary",
        variant: "solid",
        class:
          "text-primary hover:not-disabled:bg-primary hover:not-disabled:text-secondary active:bg-primary active:text-secondary in-inverted:text-default in-inverted:active:text-default in-inverted:hover:not-disabled:text-default",
      },
      {
        color: "success",
        variant: "solid",
        class: "hover:not-disabled:bg-white hover:not-disabled:text-success active:bg-white active:text-success",
      },
      {
        color: "error",
        variant: "solid",
        class: "hover:not-disabled:bg-white hover:not-disabled:text-error active:bg-white active:text-error",
      },
      {
        color: "neutral",
        variant: "solid",
        class: "hover:not-disabled:bg-white hover:not-disabled:text-neutral-700 active:bg-white active:text-neutral-700",
      },
      {
        color: "blue",
        variant: "solid",
        class: "hover:not-disabled:bg-primary active:bg-primary",
      },
      {
        color: "blue",
        variant: "outline",
        class: {
          base: `text-primary ring-primary-400 hover:not-disabled:ring-primary-400 hover:not-disabled:bg-primary-300 in-elevated:hover:not-disabled:ring-primary-500 in-elevated:hover:not-disabled:bg-primary-400 ${INPUT_HAS_FOCUS_VISIBLE} ${INPUT_FOCUS_COLOR_PRIMARY}`,
          leadingIcon: "text-blue-700",
          trailingIcon: "text-blue-700",
        },
      },
      {
        color: "blue",
        variant: "soft",
        class: {
          base: "text-primary hover:bg-blue-300",
          leadingIcon: "text-blue-700",
          trailingIcon: "text-blue-700",
        },
      },
      {
        color: "primary",
        variant: "outline",
        class:
          "hover:not-disabled:bg-primary hover:not-disabled:text-white active:bg-primary active:text-white in-inverted:hover:not-disabled:text-default in-inverted:active:text-default disabled:opacity-100 disabled:ring-primary/20",
      },
      {
        color: "secondary",
        variant: "outline",
        class:
          "hover:not-disabled:bg-secondary hover:not-disabled:text-white active:bg-secondary active:text-white in-inverted:hover:not-disabled:text-secondary in-inverted:active:text-default disabled:opacity-100 disabled:ring-secondary/20",
      },
      {
        color: "success",
        variant: "outline",
        class: "hover:not-disabled:bg-success hover:not-disabled:text-white active:bg-success active:text-white",
      },
      {
        color: "error",
        variant: "outline",
        class: "hover:not-disabled:bg-error hover:not-disabled:text-white active:bg-error active:text-white",
      },
      {
        color: "neutral",
        variant: "outline",
        class: "hover:not-disabled:bg-neutral-500 hover:not-disabled:text-white active:bg-neutral-500 active:text-white",
      },
      {
        color: "primary",
        variant: "soft",
        class:
          "in-white:bg-primary-200 in-elevated:bg-white text-primary ring-0 hover:not-disabled:bg-primary-300 in-elevated:hover:not-disabled:bg-primary-400 active:bg-primary-200 focus-visible:bg-primary-200 has-focus-visible:bg-primary-200",
      },
      {
        color: "neutral",
        variant: "soft",
        class:
          "bg-white text-primary ring-0 hover:not-disabled:bg-blue-400 focus-visible:bg-white has-focus-visible:bg-white active:bg-white in-inverted:bg-blue-800/70 in-inverted:not-disabled:bg-blue-800/90",
      },
      {
        color: "primary",
        variant: "ghost",
        class: "hover:not-disabled:bg-elevated hover:not-disabled:text-elevated active:bg-elevated active:text-elevated disabled:text-gray-400",
      },
      {
        color: "secondary",
        variant: "ghost",
        class: "hover:not-disabled:bg-accented hover:not-disabled:text-accented active:bg-accented active:text-accented",
      },
      {
        variant: "link",
        class: "p-1 -m-1 gap-1 underline decoration-1 underline-offset-[.45em]",
      },
      {
        color: "primary",
        variant: "link",
        class: "hover:not-disabled:text-blue-700 in-inverted:hover:not-disabled:text-dimmed",
      },
      {
        color: "blue",
        variant: "link",
        class: "hover:not-disabled:text-primary in-inverted:text-primary in-inverted:hover:not-disabled:text-dimmed",
      },
      {
        color: "primary",
        variant: "subtle",
        class: "ring-primary-400 bg-white hover:bg-primary-100 active:bg-primary-100 disabled:bg-primary/10 aria-disabled:bg-primary/10",
      },
    ],
  },
  badge: {
    variants: {
      size: {
        xs: {
          base: "text-sm px-2 py-1 gap-1 rounded-sm",
          leadingIcon: "size-3",
          leadingAvatarSize: "3xs",
          trailingIcon: "size-3",
        },
        sm: {
          base: "text-sm/5.5 px-2 py-1 gap-1 rounded-sm",
          leadingIcon: "size-3",
          leadingAvatarSize: "3xs",
          trailingIcon: "size-3",
        },
        md: {
          base: "text-base/6 px-2 py-1 gap-1 rounded-sm",
          leadingIcon: "size-4",
          leadingAvatarSize: "3xs",
          trailingIcon: "size-4",
        },
        lg: {
          base: "text-base/6.5 font-semibold px-2.5 py-1.5 gap-1.5 rounded-md",
          leadingIcon: "size-5",
          leadingAvatarSize: "2xs",
          trailingIcon: "size-5",
        },
        xl: {
          base: "text-base/6.5 font-semibold px-3 py-2 gap-1.5 rounded-md",
          leadingIcon: "size-6",
          leadingAvatarSize: "2xs",
          trailingIcon: "size-6",
        },
      },
    },
    compoundVariants: [
      {
        color: "primary",
        variant: "soft",
        class: "bg-primary-300 text-primary in-inverted:bg-primary/12",
      },
      {
        color: "neutral",
        variant: "soft",
        class: "bg-white text-primary ring-0",
      },
      {
        color: "success",
        variant: "soft",
        class: "bg-success-100",
      },
      {
        color: "error",
        variant: "soft",
        class: "bg-error-100",
      },
    ],
  },
  popover: {
    slots: {
      content: "rounded-xl",
    },
  },
  modal: {
    slots: {
      overlay: "",
      content: "w-full rounded-lg divide-lagoon-300",
      header: "px-4 sm:px-4 pt-5 pb-3 border-b-0 min-h-auto",
      title: "text-2xl font-semibold text-default",
      body: "px-4 sm:px-4 pt-4 sm:pt-4 pb-10 sm:pb-10",
      footer: "p-4 sm:p-4 border-none",
      close: "top-2 end-2 size-8 flex items-center justify-center",
    },
    variants: {
      overlay: {
        true: {
          overlay: "bg-inverted/90",
        },
      },
      fullscreen: {
        false: {
          content: "sm:max-w-md",
        },
      },
    },
  },
  slideover: {
    slots: {
      overlay: "data-[state=open]:animate-none! data-[state=closed]:animate-none!",
      content: "data-[state=open]:animate-none! data-[state=closed]:animate-none!",
    },
    variants: {
      side: {
        left: { content: "data-[state=open]:animate-none! data-[state=closed]:animate-none!" },
        right: { content: "data-[state=open]:animate-none! data-[state=closed]:animate-none!" },
        top: { content: "data-[state=open]:animate-none! data-[state=closed]:animate-none!" },
        bottom: { content: "data-[state=open]:animate-none! data-[state=closed]:animate-none!" },
      },
    },
  },
  alert: {
    slots: {
      root: "rounded-lg p-3 gap-0 ring-0",
      title: "text-base/5.5 font-semibold text-current",
      description: "text-base/5.5 text-current opacity-100",
    },
    defaultVariants: {
      variant: "soft",
    },
    variants: {
      title: {
        true: {
          description: "mt-0 font-normal",
        },
      },
    },
    compoundVariants: [
      {
        color: "success",
        variant: ["soft", "subtle"],
        class: { root: "bg-green-200 text-primary" },
      },
      {
        color: "error",
        variant: ["soft", "subtle"],
        class: { root: "bg-red-100 text-primary" },
      },
      {
        color: "neutral",
        variant: ["soft", "subtle"],
        class: { root: "bg-gray-50 text-gray-600" },
      },
      {
        color: "primary",
        variant: ["soft", "subtle"],
        class: { root: "bg-primary-100 text-primary" },
      },
      {
        color: "success",
        variant: "subtle",
        class: { root: "ring-green-300" },
      },
      {
        color: "error",
        variant: "subtle",
        class: { root: "ring-red-300" },
      },
      {
        color: "neutral",
        variant: "subtle",
        class: { root: "ring-gray-200" },
      },
      {
        color: "primary",
        variant: "subtle",
        class: { root: "ring-primary-400" },
      },
    ],
  },
  toast: {
    slots: {
      root: "rounded-md px-4 py-3 gap-4 shadow-none items-start ring-0 ring",
      wrapper: "gap-1",
      title: "text-base leading-tight font-semibold text-current",
      description: "text-sm text-primary text-current mt-0",
      icon: "size-6 -mt-0.5 shrink-0 text-current!",
      actions: "h-5",
      close: "cursor-pointer rounded size-8 p-0 min-h-0 flex items-center justify-center -mt-1.25 -mr-2.5 text-current hover:text-current hover:bg-current/10",
      progress: "hidden",
    },
    defaultVariants: {
      color: "error",
    },
    variants: {
      color: {
        primary: {
          root: "bg-primary-100 text-primary-700 ring-primary-400",
        },
        secondary: {
          root: "bg-secondary-100 text-secondary-700 ring-secondary-400",
        },
        error: {
          root: "bg-error-100 text-error-300 ring-error-200",
        },
        success: {
          root: "bg-success-100 text-success-700 ring-success-300",
        },
        warning: {
          root: "bg-gray-100 text-gray-500 ring-gray-300",
        },
      },
      title: {
        true: {
          description: "mt-0",
        },
      },
    },
  },
  dropdownMenu: {
    slots: {
      content: "ring-0 shasow-none min-w-auto",
      item: "font-semibold text-sm/5 px-2 py-1",
      group: "p-0",
    },
  },
  accordion: {
    slots: {
      item: "border-b border-blue-300 last:border-b-0",
      header: "py-1",
      trigger: "cursor-pointer hover:bg-blue-100 rounded py-3 text-lg font-semibold text-primary pr-0.5",
      trailingIcon: "size-5 text-primary",
      body: "pb-4 pt-0 px-0.5",
    },
  },
  skeleton: {
    base: "bg-primary-400/25",
  },
};
